import React, { PropsWithChildren, useEffect } from 'react';
import { Store } from '@reduxjs/toolkit';
import { Provider } from 'react-redux';
import {
  ImageProvider,
  type ImageRegistry,
} from '../../components/ImageContext';
import { preconnectCdn } from '../../lib';
import { ensureReady as ensureBetSlipReady } from '../../services/betSlip';
import AuthProvider from '../AuthProvider';
import StompProvider from '../StompProvider';
import ThemeProvider from '../ThemeProvider';
import ToastProvider from '../ToastProvider';

export interface RootProviderProps {
  images: ImageRegistry;
  // local bundled URLs used as fallback when CDN is down — defaults to images
  localImages?: ImageRegistry;
  intlWrapper?: React.ComponentType<{ children: React.ReactNode }>;
  store: Store;
}

const RootProvider: React.FC<PropsWithChildren<RootProviderProps>> = ({
  children,
  images,
  localImages,
  intlWrapper: IntlWrapper,
  store,
}) => {
  // Warm the CDN connection on mount, before the game loads its assets.
  useEffect(() => {
    preconnectCdn();
    void ensureBetSlipReady();
  }, []);

  const content = (
    <ImageProvider images={images} localImages={localImages ?? images}>
      <ToastProvider>
        <Provider store={store}>
          <StompProvider>
            <AuthProvider>
              <ThemeProvider>{children}</ThemeProvider>
            </AuthProvider>
          </StompProvider>
        </Provider>
      </ToastProvider>
    </ImageProvider>
  );

  if (IntlWrapper) {
    return <IntlWrapper>{content}</IntlWrapper>;
  }

  return content;
};

export default RootProvider;
